import React, { useMemo } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

interface MathTextRendererProps {
  text: string;
  /** Optional CSS class for the container */
  className?: string;
  /** Optional inline styles */
  style?: React.CSSProperties;
  /** Font size variant */
  fontSize?: string;
}

/**
 * Parses a string for $...$ (inline) and $$...$$ (display) LaTeX delimiters,
 * renders the math portions via KaTeX, and returns the mixed HTML.
 *
 * Normal text without any $ delimiters renders as-is (backward compatible).
 */
const MathTextRenderer: React.FC<MathTextRendererProps> = ({
  text,
  className,
  style,
  fontSize,
}) => {
  const renderedHTML = useMemo(() => {
    if (!text) return '';

    // Check if text contains any $ — if not, skip regex entirely for performance
    if (!text.includes('$')) {
      return escapeHtml(text);
    }

    return renderMathInText(text);
  }, [text]);

  return (
    <span
      className={className}
      // `pre-wrap` preserves author-entered newlines (Enter → real line break)
      // while still wrapping long lines. Overridable via the `style` prop.
      style={{ fontSize, whiteSpace: 'pre-wrap', ...style }}
      dangerouslySetInnerHTML={{ __html: renderedHTML }}
    />
  );
};

/**
 * Escape HTML special characters in plain text segments.
 */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Parse text for $$...$$ (display math) and $...$ (inline math) delimiters,
 * and render the LaTeX portions using KaTeX.
 */
function renderMathInText(text: string): string {
  // Match $$...$$ for display math first, then $...$ for inline math.
  // Uses a regex that matches $$ first (greedy for display), then single $.
  const parts: string[] = [];
  let lastIndex = 0;

  // Pattern: match $$...$$  or  $...$  (non-greedy inside)
  const mathRegex = /\$\$([\s\S]*?)\$\$|\$((?!\$)(.*?)(?<!\$))\$/g;
  let match: RegExpExecArray | null;

  while ((match = mathRegex.exec(text)) !== null) {
    // Add the text before this match
    if (match.index > lastIndex) {
      parts.push(escapeHtml(text.slice(lastIndex, match.index)));
    }

    const isDisplay = match[1] !== undefined;
    const latex = isDisplay ? match[1] : match[2];

    try {
      const html = katex.renderToString(latex.trim(), {
        throwOnError: false,
        displayMode: isDisplay,
        output: 'html',
      });
      parts.push(html);
    } catch {
      // If KaTeX fails, show the raw LaTeX
      parts.push(escapeHtml(match[0]));
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after the last match
  if (lastIndex < text.length) {
    parts.push(escapeHtml(text.slice(lastIndex)));
  }

  return parts.join('');
}

export default React.memo(MathTextRenderer);
